import { apiFetch } from '@/lib/api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

type PublicLot = {
  title: string;
  status: string;
  sale_type?: 'live_animal' | 'carcass_share' | 'retail_cuts';
  sale_type_label?: string;
  availability_label?: string;
  interest_qty_label?: string;
  slaughter_date: string | null;
  pickup_location: string | null;
  notes: string | null;
  total_weight_kg: number | null;
  progress_percent: number;
  shares_total: number;
  shares_available: number;
  sold_out: boolean;
  farm_name: string;
  farm?: {
    name: string;
    legal_name: string | null;
    logo_url: string | null;
    email: string | null;
    phone: string | null;
    location: string | null;
  };
  animal: {
    name: string | null;
    kind: string | null;
    breed: string | null;
    cover_url: string | null;
  };
  shares: Array<{
    id: number;
    fraction_display: string;
    fraction_label: string;
    display_label?: string;
    cut_type?: string | null;
    quantity?: string | number | null;
    weight_kg: number | null;
    price: string | number;
  }>;
};

export function PublicSalePage() {
  const { slug = '' } = useParams();
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    shares_wanted: '1',
    message: '',
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-sales', slug],
    queryFn: () =>
      apiFetch<{ lot: PublicLot }>(`/public/sales/${slug}`).then((d) => d.lot),
    enabled: !!slug,
  });

  const submit = useMutation({
    mutationFn: () =>
      apiFetch<{ message: string }>(`/public/sales/${slug}/leads`, {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          shares_wanted: Number(form.shares_wanted) || 1,
        }),
      }),
    onSuccess: () => setDone(true),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center text-stone-500">
        Carregando…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center text-stone-600">
        Lote não encontrado.
      </div>
    );
  }

  const farm = data.farm;
  const farmName = farm?.name || data.farm_name;
  const farmContacts = [farm?.phone, farm?.email].filter(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-stone-50 text-stone-900">
      <div className="mx-auto max-w-lg px-4 py-8 space-y-6">
        <header className="space-y-4">
          <div className="flex items-center gap-3">
            {farm?.logo_url ? (
              <img
                src={farm.logo_url}
                alt={farmName}
                className="h-14 w-14 shrink-0 rounded-2xl bg-white object-contain shadow-sm ring-1 ring-stone-200"
              />
            ) : (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-emerald-900 text-lg font-bold text-white shadow-sm">
                {farmName.slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-base font-semibold text-stone-900">{farmName}</p>
              {farm?.legal_name && farm.legal_name !== farmName && (
                <p className="truncate text-xs text-stone-500">{farm.legal_name}</p>
              )}
              {farm?.location && (
                <p className="truncate text-xs text-stone-500">{farm.location}</p>
              )}
              {farmContacts.length > 0 && (
                <p className="truncate text-xs text-stone-500">{farmContacts.join(' · ')}</p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">{data.title}</h1>
            {data.sold_out && (
              <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                Esgotado / lista de espera
              </span>
            )}
          </div>
        </header>

        {data.animal.cover_url && (
          <img
            src={data.animal.cover_url}
            alt={data.animal.name || data.title}
            className="aspect-[4/3] w-full rounded-2xl object-cover shadow"
          />
        )}

        <div className="rounded-2xl bg-white p-4 shadow-sm space-y-2 text-sm">
          <p>
            <strong>Animal:</strong> {data.animal.name || '—'}
            {data.animal.breed ? ` · ${data.animal.breed}` : ''}
          </p>
          <p>
            <strong>Abate:</strong> {data.slaughter_date || 'A definir'}
          </p>
          <p>
            <strong>Retirada:</strong> {data.pickup_location || 'A combinar'}
          </p>
          {data.notes && <p className="text-stone-600">{data.notes}</p>}
          <p className="text-stone-500">
            {data.availability_label || 'Cotas disponíveis'}: {data.shares_available} de{' '}
            {data.shares_total}
          </p>
          <div className="h-2 overflow-hidden rounded-full bg-stone-100">
            <div
              className="h-full rounded-full bg-emerald-700"
              style={{ width: `${Math.min(100, data.progress_percent)}%` }}
            />
          </div>
        </div>

        {!data.sold_out && (
          <div className="grid gap-2">
            {data.shares.map((share) => (
              <div
                key={share.id}
                className="flex items-center justify-between rounded-xl bg-white px-4 py-3 shadow-sm"
              >
                <div>
                  <div className="font-medium">
                    {share.display_label || share.fraction_label}
                  </div>
                  {share.weight_kg && (
                    <div className="text-xs text-stone-500">~{share.weight_kg} kg</div>
                  )}
                </div>
                <div className="font-semibold">
                  R${' '}
                  {Number(share.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>
            ))}
          </div>
        )}

        <section className="rounded-2xl bg-white p-4 shadow-sm space-y-3">
          <h2 className="font-semibold">
            {data.sold_out ? 'Entrar na lista de espera' : 'Tenho interesse'}
          </h2>
          {done ? (
            <p className="text-sm text-emerald-800">
              {submit.data?.message ||
                'Recebemos seu interesse. O produtor entrará em contato.'}
            </p>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                submit.mutate();
              }}
            >
              <input
                className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"
                placeholder="Seu nome"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
              <input
                className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"
                placeholder="Telefone / WhatsApp"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              />
              <input
                className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"
                placeholder="E-mail"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
              <input
                className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"
                placeholder={data.interest_qty_label || 'Quantas cotas?'}
                type="number"
                min={1}
                value={form.shares_wanted}
                onChange={(e) => setForm((f) => ({ ...f, shares_wanted: e.target.value }))}
              />
              <textarea
                className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"
                placeholder="Mensagem (opcional)"
                rows={3}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              />
              <button
                type="submit"
                disabled={submit.isPending}
                className="w-full rounded-xl bg-emerald-800 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                {submit.isPending ? 'Enviando…' : 'Enviar interesse'}
              </button>
              {submit.isError && (
                <p className="text-sm text-red-600">
                  {(submit.error as Error).message || 'Falha ao enviar'}
                </p>
              )}
            </form>
          )}
        </section>

        <footer className="pb-8 text-center text-xs text-stone-400">
          Página gerada por ManejAI · venda direta com o produtor
        </footer>
      </div>
    </div>
  );
}
